import {
  DAILY_GROUP_ORDER,
  formatGroupLabel,
} from "./dashboardDailyGroupColumns";

/**
 * Daily Consult Performance — consultant-wise, same metric columns as
 * Daily Group Performance (minus Total Consultants).
 * Order: Consultant -> Group -> Attempts -> Missed -> Canceled -> Completed ->
 * consultation stage -> same-day conversion -> spending.
 */

function consultGroupSortKey(row) {
  const key = String(
    row?.group ?? row?.groupName ?? row?.groupCode ?? row?.groupId ?? ""
  )
    .toLowerCase()
    .trim();
  if (key === "new") return "n";
  return key;
}

function consultGroupOrderIndex(row) {
  const key = consultGroupSortKey(row);
  const idx = DAILY_GROUP_ORDER.indexOf(key);
  return idx === -1 ? DAILY_GROUP_ORDER.length : idx;
}

/**
 * Flattens API result into a consultant list ordered by group (A → B → C → N → Dead),
 * then by consultant name within each group.
 */
export function normalizeDailyConsultPerformanceData(result = {}) {
  const raw =
    result.dailyConsultantPerformance ??
    result.dailyConsultPerformance ??
    result.consultants ??
    result.data ??
    result.list ??
    result;

  let rows = [];
  if (Array.isArray(raw)) {
    rows = raw;
  } else if (raw && typeof raw === "object") {
    // Group-keyed object: { a: [...], b: [...], ... } or { a: { consultants: [...] } }
    const collected = [];
    for (const groupKey of DAILY_GROUP_ORDER) {
      const aliases = groupKey === "n" ? ["n", "new"] : [groupKey];
      for (const alias of aliases) {
        const bucket = raw[alias];
        if (!bucket) continue;
        if (Array.isArray(bucket)) {
          collected.push(
            ...bucket.map((row) => ({ ...row, group: row.group ?? groupKey }))
          );
        } else if (Array.isArray(bucket.consultants)) {
          collected.push(
            ...bucket.consultants.map((row) => ({
              ...row,
              group: row.group ?? groupKey,
            }))
          );
        } else if (typeof bucket === "object") {
          collected.push({ ...bucket, group: bucket.group ?? groupKey });
        }
      }
    }
    if (collected.length) {
      rows = collected;
    } else if (Array.isArray(raw.rows)) {
      rows = raw.rows;
    }
  }

  return [...rows].sort((a, b) => {
    const groupDiff = consultGroupOrderIndex(a) - consultGroupOrderIndex(b);
    if (groupDiff !== 0) return groupDiff;
    const nameA = String(a.consultantName ?? a.name ?? "").toLowerCase();
    const nameB = String(b.consultantName ?? b.name ?? "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

const pair = (totalCol, uniqueCol) => [totalCol, uniqueCol];

export const CONSULT_COLUMN_DEFINITIONS = [
  {
    key: "consultantName",
    label: "Consultant Name",
    type: "consultant",
    description:
      "Astrologer's name — this row shows only this consultant's own numbers.",
    example: "Astro A 3 row shows metrics only for that one astrologer.",
  },
  {
    key: "group",
    label: "Group",
    type: "group",
    description:
      "Which team of astrologers this consultant belongs to — Group A, B, C, N (new astrologers), or Dead.",
    example: "Astro A 3 belongs to Group A.",
  },

  ...pair(
    {
      key: "totalConsultationAttempt",
      label: "Total Consultation Attempt",
      type: "number",
      metricGroupStart: true,
      description:
        "How many times customers tried to connect with this consultant — chat or call, successful or not. If one customer tries 3 times, all 3 are counted.",
      example:
        "Customer books chat, cancels, then books call → Total Attempt = 2.",
    },
    {
      key: "uniqueConsultationAttempt",
      label: "Unique Consultation Attempt",
      type: "number",
      className: "primary",
      description:
        "How many different customers tried at least once with this consultant. Same customer trying multiple times still counts as 1 person here.",
      example: "3 attempts from 2 customers → Unique = 2.",
    }
  ),

  ...pair(
    {
      key: "totalMissedConsultation",
      label: "Total Missed Consultation",
      type: "number",
      className: "error",
      metricGroupStart: true,
      description:
        "Sessions where this consultant did not pick up — customer waited but call/chat was not answered. This is the astrologer's miss, not the customer canceling.",
      example:
        "Consultant misses the same customer's call twice → Missed = 2.",
    },
    {
      key: "uniqueMissedConsultation",
      label: "Unique Missed Consultation",
      type: "number",
      className: "error",
      description:
        "How many different customers had at least one missed session with this consultant.",
      example: "3 missed sessions from 2 customers → Unique Missed = 2.",
    }
  ),

  ...pair(
    {
      key: "totalCanceledConsultation",
      label: "Total Canceled Consultation",
      type: "number",
      className: "error",
      metricGroupStart: true,
      description:
        "Sessions the customer canceled themselves with this consultant — before or during the session. Does not include astrologer missed calls.",
      example:
        "Customer cancels chat before connecting → counts here, not in Missed.",
    },
    {
      key: "uniqueCanceledConsultation",
      label: "Unique Canceled Consultation",
      type: "number",
      className: "error",
      description:
        "How many different customers canceled at least one session with this consultant.",
      example: "2 customers each cancel once → Unique Canceled = 2.",
    }
  ),

  ...pair(
    {
      key: "totalCompletedConsultation",
      label: "Total Completed Consultation",
      type: "number",
      className: "success",
      metricGroupStart: true,
      description:
        "Sessions that actually finished with this consultant — both free (5-minute trial) and paid (chat or call). Gifts are not counted here; gifts are separate spending.",
      example:
        "2 free chats + 3 paid calls completed → Total Completed = 5.",
    },
    {
      key: "uniqueCompletedConsultation",
      label: "Unique Completed Consultation",
      type: "number",
      className: "success",
      description:
        "How many different customers completed at least one session (free or paid) with this consultant.",
      example: "5 completed sessions from 3 customers → Unique Completed = 3.",
    }
  ),

  {
    key: "firstConsultation",
    label: "1st Consultation",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Customer's first paid offer session that completed with this consultant — special introductory price on chat or call. Free 5-minute trial does not count. Second-offer sessions also do not count here.",
    example:
      "Customer does first-offer chat at intro price → counts 1. Free trial chat does not count.",
  },
  {
    key: "firstConsultationRevenue",
    label: "1st Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money customers spent on those first-offer sessions with this consultant (chat or call). Only consultation charges — gifts, meditation, vastu, and other products are not included.",
    example:
      "First-offer call earns ₹300 → shows ₹300 here. ₹100 gift on same day is not added.",
  },
  {
    key: "secondConsultation",
    label: "2nd Consultation",
    type: "number",
    className: "primary",
    description:
      "Customer's second special-offer session that completed with this consultant — usually the discounted audio/call offer after the first offer. Free trial and first offer do not count here.",
    example:
      "Customer completes second-offer call → counts 1 in 2nd Consultation.",
  },
  {
    key: "secondConsultationRevenue",
    label: "2nd Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money spent on second-offer consultation sessions with this consultant only. Gifts and other products are not included.",
    example:
      "Second-offer call costs ₹250 → shows ₹250 here. Gift money is separate.",
  },
  {
    key: "thirdConsultation",
    label: "3rd Consultation",
    type: "number",
    className: "primary",
    description:
      "Customer's 3rd paid session with this consultant — the session right after they finished first offer and second offer. Can be chat or call at regular price.",
    example:
      "After first-offer chat and second-offer call, customer does a normal paid chat → that is the 3rd consultation.",
  },
  {
    key: "thirdConsultationRevenue",
    label: "3rd Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money customers spent on their 3rd consultation sessions with this consultant. Consultation charges only — no gifts or other products.",
    example: "3rd session costs ₹200 → shows ₹200 here.",
  },
  {
    key: "fourthConsultation",
    label: "4th & Onwards Consultation",
    type: "number",
    className: "primary",
    description:
      "All paid sessions from the 4th visit onwards with this consultant — repeat customers coming back after first offer, second offer, and third session.",
    example:
      "Customer's 5th paid session with the astrologer → counts in 4th & Onwards.",
  },
  {
    key: "fourthConsultationRevenue",
    label: "4th & Onwards Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money earned from 4th and later consultation sessions with this consultant. Consultation only — gifts not included.",
    example:
      "4th and later sessions together earn ₹500 → shows ₹500 here.",
  },
  {
    key: "otherSpending",
    label: "Other Spending",
    type: "currency",
    className: "primary",
    description:
      "Wallet spending linked to this consultant but not tied to a ranked private-call consultation, such as gifts, meditation, vastu, session bookings, refunds, queries, or unlinked debits. This plus all four consultation-spending columns equals Total Spending.",
    example:
      "₹800 consultation spending + ₹200 gifts/services → Other Spending = ₹200 and Total Spending = ₹1,000.",
  },
  {
    key: "queryCompletedConsultation",
    label: "Query Completed",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Written query questions that this consultant answered and marked complete. Separate from chat/call offer funnel.",
    example:
      "Customer asks a question in writing and astrologer completes the answer → counts 1.",
  },
  {
    key: "queryConvertedUsers",
    label: "Query Converted",
    type: "number",
    className: "success",
    description:
      "Completed queries that converted: the user then recharged and finished their first paid consultation with this same consultant.",
    example:
      "Astrologer completes a written query, the user recharges, and then completes a paid chat or call with the same astrologer → counts 1.",
  },
  {
    key: "giftPayout",
    label: "Gift Payout (50%)",
    type: "currency",
    className: "primary",
    description: "Consultant's 50% earning from gifts received in the selected period.",
    example: "User sends a ₹200 gift → consultant payout is ₹100.",
  },
  {
    key: "sameDayRepeatConsultation",
    label: "Same Day 1st to 2nd Conversion",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Customers who did both first offer and second offer on the same day with this consultant. Shows how many people upgraded quickly in one day.",
    example:
      "Morning first-offer chat + evening second-offer call, same day → adds 1.",
  },
  {
    key: "sameDayConversionPct",
    label: "Same Day 1st to 2nd Conversion %",
    type: "percentage",
    description:
      "Out of all first-offer completions with this consultant, what percentage also did second offer the same day. Higher % means customers are converting faster.",
    example: "10 first offers and 3 same-day upgrades → 30%.",
  },
  {
    key: "recentUserRevenue",
    label: "Selected Range Registered User Revenue",
    type: "currency",
    metricGroupStart: true,
    description:
      "Consultant earning credits generated inside the selected period by users who registered inside that same selected date range.",
    example:
      "For a 22–28 July filter, only users registered during 22–28 July contribute revenue here.",
  },
  {
    key: "existingUserRevenue",
    label: "Revenue Before Selected Range",
    type: "currency",
    description:
      "Consultant earning credits generated inside the selected period by older users who registered before the selected range started.",
    example:
      "For a 22–28 July filter, revenue earned during 22–28 July from users registered before 22 July contributes here.",
  },
  {
    key: "totalSpending",
    label: "Total Spending",
    type: "currency",
    metricGroupStart: true,
    omitOnMarketingDashboard: true,
    description:
      "All money customers spent linked to this consultant — consultations, gifts, meditation, vastu, booked sessions, and refunds. Wider than consultation revenue columns because it includes gifts and other products too.",
    example:
      "₹800 on consult + ₹200 gift + ₹200 meditation → Total Spending = ₹1,200.",
  },
];

/** @deprecated Use CONSULT_COLUMN_DEFINITIONS */
export const CONSULTANT_COLUMN_DEFINITIONS = CONSULT_COLUMN_DEFINITIONS;

export function getConsultColumnDefinitions(isMarketingDashboard) {
  if (!isMarketingDashboard) return CONSULT_COLUMN_DEFINITIONS;
  return CONSULT_COLUMN_DEFINITIONS.filter((c) => !c.omitOnMarketingDashboard);
}

/** @deprecated Use getConsultColumnDefinitions */
export function getConsultantColumnDefinitions(isMarketingDashboard) {
  return getConsultColumnDefinitions(isMarketingDashboard);
}

export function buildConsultColumnVisibilityDefaults() {
  const vis = {};
  CONSULT_COLUMN_DEFINITIONS.forEach((col) => {
    vis[col.key] = true;
  });
  return vis;
}

/** Columns kept on when Sub group is AQ, BQ, CQ, or NQ. Everything else is hidden. */
export const QUERY_SUBGROUP_VISIBLE_COLUMN_KEYS = [
  "consultantName",
  "group",
  "queryCompletedConsultation",
  "queryConvertedUsers",
];

export function buildQuerySubGroupColumnVisibility() {
  const vis = {};
  CONSULT_COLUMN_DEFINITIONS.forEach((col) => {
    vis[col.key] = QUERY_SUBGROUP_VISIBLE_COLUMN_KEYS.includes(col.key);
  });
  return vis;
}

/** @deprecated Use buildConsultColumnVisibilityDefaults */
export function buildConsultantColumnVisibilityDefaults() {
  return buildConsultColumnVisibilityDefaults();
}

export function getConsultCellValue(row, column) {
  switch (column.type) {
    case "consultant":
      return row.consultantName ?? row.name ?? "";
    case "group":
      return formatGroupLabel(
        row.group ?? row.groupName ?? row.groupCode ?? row.groupId
      );
    case "percentage":
      return row[column.key] ?? 0;
    case "currency":
      return `₹${(row[column.key] || 0).toLocaleString()}`;
    default:
      return row[column.key] || 0;
  }
}

export function getConsultTotalsValue(column, totalsOrRows) {
  const dataArray = Array.isArray(totalsOrRows)
    ? totalsOrRows
    : totalsOrRows
      ? [totalsOrRows]
      : [];

  switch (column.type) {
    case "percentage": {
      if (column.key === "sameDayConversionPct") {
        const totalFirst = dataArray.reduce(
          (sum, row) => sum + (row.firstConsultation || 0),
          0
        );
        const totalSameDay = dataArray.reduce(
          (sum, row) => sum + (row.sameDayRepeatConsultation || 0),
          0
        );
        if (totalFirst === 0) return 0;
        return Math.round((totalSameDay / totalFirst) * 10000) / 100;
      }
      if (dataArray.length === 0) return 0;
      const sum = dataArray.reduce(
        (s, row) => s + (row[column.key] ?? 0),
        0
      );
      return Math.round((sum / dataArray.length) * 100) / 100;
    }
    case "currency":
      return `₹${dataArray
        .reduce((sum, row) => sum + (row[column.key] || 0), 0)
        .toLocaleString()}`;
    default:
      if (
        column.key === "consultantName" ||
        column.key === "group" ||
        column.type === "consultant" ||
        column.type === "group"
      ) {
        return "TOTALS";
      }
      return dataArray.reduce((sum, row) => sum + (row[column.key] || 0), 0);
  }
}
